import type { AnnotationData, AnnotationShape, WireColor } from "../api/types";

/** Sim ranglari (backend `core/svgutil.py` bilan bir xil bo'lishi shart). */
export const WIRE_COLORS = { phase: "#dc2626", neutral: "#2563eb", ground: "#65a30d", other: "#7c3aed" } as const;
export type NamedColor = keyof typeof WIRE_COLORS;
export const NAMED_COLORS = Object.keys(WIRE_COLORS) as NamedColor[];

const HEX = /^#[0-9a-fA-F]{6}$/;
export const MAX_SHAPES = 500; // backend `annotation_schema.MAX_SHAPES`
export const MAX_POINTS = 500;

export function resolveColor(c: WireColor | undefined): string {
  if (c && c in WIRE_COLORS) return WIRE_COLORS[c as NamedColor];
  return c && HEX.test(c) ? c : WIRE_COLORS.other;
}

/** Chiziq qalinligi va matn o'lchami rasmning katta tomoniga nisbatan (backend `overlay.py` bilan bir xil). */
export const strokePx = (w: number, h: number) => Math.max(w, h) * 0.004;
export const fontPx = (w: number, h: number) => Math.max(w, h) * 0.03;

export type Pt = [number, number];

let counter = 0;
export function newId(prefix = "s"): string {
  const rnd =
    typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return `${prefix}${++counter}-${rnd}`;
}

export const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function shapePoints(s: AnnotationShape): Pt[] {
  return s.type === "text" ? [[s.x, s.y]] : s.points;
}

/** Shaklni (dx, dy) ga suradi; hech bir nuqta 0..1 dan chiqmasligi uchun siljish cheklanadi. */
export function moveShape(s: AnnotationShape, dx: number, dy: number): AnnotationShape {
  const pts = shapePoints(s);
  const minX = Math.min(...pts.map((p) => p[0]));
  const maxX = Math.max(...pts.map((p) => p[0]));
  const minY = Math.min(...pts.map((p) => p[1]));
  const maxY = Math.max(...pts.map((p) => p[1]));
  const ddx = Math.min(1 - maxX, Math.max(-minX, dx));
  const ddy = Math.min(1 - maxY, Math.max(-minY, dy));
  if (s.type === "text") return { ...s, x: s.x + ddx, y: s.y + ddy };
  if (s.type === "dimension") {
    const [a, b] = s.points;
    return { ...s, points: [[a[0] + ddx, a[1] + ddy], [b[0] + ddx, b[1] + ddy]] };
  }
  return { ...s, points: s.points.map(([x, y]) => [x + ddx, y + ddy] as Pt) };
}

export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const [px, py] = p;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / len2));
  return Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy));
}

/**
 * Nuqta shaklga tegadimi? Hisob rasm pikselida (x*W, y*H), shuning uchun aylana to'g'ri chiqadi.
 * `tolPx` — sezgirlik radiusi (piksel).
 */
export function hitShape(s: AnnotationShape, p: Pt, tolPx: number, w: number, h: number): boolean {
  const px: Pt = [p[0] * w, p[1] * h];
  if (s.type === "text") {
    const size = fontPx(w, h);
    const x = s.x * w;
    const y = s.y * h;
    const width = s.text.length * size * 0.6;
    return px[0] >= x - tolPx && px[0] <= x + width + tolPx && px[1] >= y - size - tolPx && px[1] <= y + tolPx;
  }
  const pts = s.points.map(([x, y]) => [x * w, y * h] as Pt);
  for (let i = 0; i < pts.length - 1; i++) {
    if (distToSegment(px, pts[i]!, pts[i + 1]!) <= tolPx) return true;
  }
  return false;
}

/** Eng yuqoridagi (oxirgi chizilgan) tegilgan shaklni topadi. */
export function pickShape(shapes: AnnotationShape[], p: Pt, tolPx: number, w: number, h: number): AnnotationShape | null {
  for (let i = shapes.length - 1; i >= 0; i--) {
    if (hitShape(shapes[i]!, p, tolPx, w, h)) return shapes[i]!;
  }
  return null;
}

/** Tasodifan bosish bilan paydo bo'lgan "nuqta"larni chiqarib tashlash uchun. */
export function longEnough(a: Pt, b: Pt, w: number, h: number, minPx = 8): boolean {
  return Math.hypot((a[0] - b[0]) * w, (a[1] - b[1]) * h) >= minPx;
}

export const canAddShape = (data: AnnotationData) => data.shapes.length < MAX_SHAPES;

const isNum01 = (v: unknown): v is number => typeof v === "number" && v >= 0 && v <= 1;
const isPt = (p: unknown): p is Pt => Array.isArray(p) && p.length === 2 && isNum01(p[0]) && isNum01(p[1]);
const isColor = (c: unknown) => c === undefined || (typeof c === "string" && (c in WIRE_COLORS || HEX.test(c)));

/** Backend `validate_annotation` bilan bir xil qoidalar: serverdan kelgan/yuboriladigan hujjatni tekshirish. */
export function isAnnotationData(x: unknown): x is AnnotationData {
  if (!x || typeof x !== "object" || Array.isArray(x)) return false;
  const o = x as Record<string, unknown>;
  if (Object.keys(o).some((k) => k !== "shapes")) return false;
  const shapes = o.shapes ?? [];
  if (!Array.isArray(shapes) || shapes.length > MAX_SHAPES) return false;
  const ids = new Set<string>();
  for (const raw of shapes) {
    if (!raw || typeof raw !== "object") return false;
    const s = raw as Record<string, unknown>;
    if (typeof s.id !== "string" || !s.id || s.id.length > 64 || ids.has(s.id)) return false;
    ids.add(s.id);
    if (!isColor(s.color)) return false;
    if (s.type === "line" || s.type === "arrow" || s.type === "polyline") {
      if (!Array.isArray(s.points) || s.points.length < 2 || s.points.length > MAX_POINTS || !s.points.every(isPt)) return false;
    } else if (s.type === "dimension") {
      if (!Array.isArray(s.points) || s.points.length !== 2 || !s.points.every(isPt)) return false;
    } else if (s.type === "text") {
      if (!isNum01(s.x) || !isNum01(s.y)) return false;
    } else return false;
    if (s.type === "text" || s.type === "dimension") {
      if (typeof s.text !== "string" || s.text.length < 1 || s.text.length > 200) return false;
    }
  }
  return true;
}
